﻿import React, { useState, useEffect } from 'react';
import { StandardReference } from '../../models/Standards/standard-reference';
import { LookupItem } from '../../models/lookup-item';
import { CrudFunctions } from '../../utilities/crud-functions';
import GlobalDialog from '../Modal/GlobalDialog';
import { ModalMetadata } from '../../models/modal-metadata';
import { ModalMessageType } from '../../models/enums';

import TextBox from 'devextreme-react/text-box';
import SelectBox from 'devextreme-react/select-box';
import NumberBox from 'devextreme-react/number-box';
import Button from 'devextreme-react/button';

export interface Props {
    productId?: string;
    onCancel?: () => void;
}

export default function AddStandardDialog(props: Props) {
    const [standardReference, setStandardReference] =
        useState<StandardReference>();

    const [standardManufacturers, setStandardManufacturers] =
        useState<LookupItem[]>();

    const [uoms, setUoms] =
        useState<LookupItem[]>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    useEffect(() => {
        getAllData();
    }, []);

    return (
        <div>
            <div className="pw-comp-row">
                <div className="pw-form-unit pw-comp-30">
                    <label className="textbox-label">Manufacturer</label>
                    <SelectBox id="manufacturerId"
                        showClearButton={true}
                        placeholder='manufacturer'
                        displayExpr='name' valueExpr="id"
                        itemRender={renderManufacturer}
                        dataSource={standardManufacturers ?
                            standardManufacturers : []}
                        onValueChanged={onStandardReferenceValueChanged}
                        value={standardReference && standardReference.manufacturerId ?
                            standardReference.manufacturerId : undefined} />
                </div>
            </div>

            <div className="pw-comp-row">
                <div className="pw-form-unit pw-comp-15">
                    <label className="textbox-label">Catalog #</label>
                    <TextBox id="catalogNumber"
                        className="textbox"
                        onValueChanged={onStandardReferenceValueChanged}
                        showClearButton={true}
                        placeholder="catalog number" value={standardReference && standardReference.catalogNumber ?
                            standardReference.catalogNumber : undefined} />
                </div>

                <div className="pw-form-unit pw-comp-15">
                    <label className="textbox-label">CAS #</label>
                    <TextBox id="casNumber" className="textbox"
                        onValueChanged={onStandardReferenceValueChanged}
                        showClearButton={true}
                        placeholder="CAS #" value={standardReference && standardReference.casNumber ?
                            standardReference.casNumber : undefined} />
                </div>
            </div>

            <div className="pw-comp-row">
                <div className="pw-form-unit pw-comp-30">
                    <label className="textbox-label">Product Name</label>
                    <TextBox id="productName"
                        className="textbox"
                        onValueChanged={onStandardReferenceValueChanged}
                        showClearButton={true}
                        placeholder="product name" value={standardReference && standardReference.productName ?
                            standardReference.productName : undefined} />
                </div>
            </div>

            <div className="pw-comp-row">
                <div className="pw-form-unit pw-comp-15">
                    <label className="textbox-label">Net Weight</label>
                    <NumberBox id="netWeight" className="textbox"
                        onValueChanged={onStandardReferenceValueChanged}
                        showClearButton={true}
                        placeholder="net weight" value={standardReference && standardReference.netWeight ?
                            standardReference.netWeight : undefined} />
                </div>
                <div className="pw-form-unit pw-comp-15">
                    <label className="textbox-label">UOM</label>
                    <SelectBox id="netWeightUom"
                        showClearButton={true}
                        placeholder='UOM'
                        valueExpr="id" displayExpr='name'
                        dataSource={uoms ? uoms : []}
                        onValueChanged={onStandardReferenceValueChanged}
                        value={standardReference && standardReference.netWeightUom ?
                            standardReference.netWeightUom : undefined} />
                </div>
            </div>

            <div className="pw-comp-row pw-margin-top-1">
                <div className="pw-centered-buttons-container">
                    <Button id="applySelectionButton"
                        className="pw-button"
                        text="Save" type="success"
                        hint="save standard"
                        height='2.6em'
                        onClick={onSaveClick} />

                    <Button id="cancelButton"
                        className="pw-button"
                        text="Cancel" type="normal"
                        hint="cancel"
                        height='2.6em'
                        onClick={onCancelClick} />
                </div>
            </div>

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} />
            }
        </div>
    );


    function onStandardReferenceValueChanged(e: any) {
        if (e.element && e.element.id) {
            const caller = e.element.id;

            setStandardReference({ ...standardReference, [caller]: e.value });
        }
    }

    function onCancelClick(e: any) {
        if (props.onCancel)
            props.onCancel();
    }

    async function onSaveClick() {
        if (standardReference)
            await saveStandard(standardReference)
                .then(() => {
                    if (props.onCancel)
                        props.onCancel();
                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Saving Standard");
                });
    }

    //GET functions

    async function getStandardsManufacturers() {
        await CrudFunctions.getData("standards/standardsmanufacturers")
            .then(data => {
                setStandardManufacturers(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Manufacturers");
            });
    }

    async function getUoms() {
        await CrudFunctions.getData("common/uoms")
            .then(data => {
                setUoms(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving UOMs");
            });
    }

    async function saveStandard(standard: StandardReference) {
        return await CrudFunctions.postData(`products/productstandardandreference/${props.productId}`, standard);
    }

    async function getAllData() {
        await Promise.all([
            getStandardsManufacturers(),
            getUoms()
        ]);
    }

    //render

    function renderManufacturer(e: any) {
        let description = e.description && ` - ${e.description}`;

        return <div>{e.name}{description}</div>
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }
}
